import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from './useSeo';
import { trackJoinDiscord } from '../lib/analytics';

// Dedicated, indexable page for the community video. Google indexes videos from
// pages that embed the video AND carry VideoObject structured data — the home
// page embeds it, but this page gives the video its own crawlable URL, title,
// and schema so it can surface in Google Video / the main results.
const VIDEO_ID = 'pzUfgtTtcF4';
const CANONICAL = 'https://jointtaskforceheavy.com/watch';

const videoSchema = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'Joint Task Force Heavy - Join the Chaos of Battle! DCS Cinematic',
  description:
    'A DCS World cinematic from Joint Task Force Heavy: coordinated combat aviation, NATO-aligned squadrons, and modern-era operations flown by our global community.',
  // Premiered Feb 19, 2026 on the JTF Heavy YouTube channel.
  uploadDate: '2026-02-19',
  thumbnailUrl: [`https://i.ytimg.com/vi/${VIDEO_ID}/hqdefault.jpg`],
  embedUrl: `https://www.youtube.com/embed/${VIDEO_ID}`,
  contentUrl: `https://www.youtube.com/watch?v=${VIDEO_ID}`,
  publisher: {
    '@type': 'Organization',
    name: 'Joint Task Force Heavy',
    logo: {
      '@type': 'ImageObject',
      url: 'https://jointtaskforceheavy.com/logo.png',
    },
  },
};

const WatchPage = () => {
  useSeo({
    title: 'Watch: JTF Heavy DCS Flight Operations | Joint Task Force Heavy',
    description:
      'Watch Joint Task Force Heavy fly DCS World: coordinated flight operations, NATO-aligned squadrons, and modern-era campaigns from a global combat aviation community.',
    canonical: CANONICAL,
  });

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoSchema) }}
      />

      {/* Hero band */}
      <section className="relative bg-hvy-900 pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/logo.webp" alt="JTF Heavy" className="mx-auto h-20 mb-6" />
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-wider text-white">
            Watch Us Fly
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Joint Task Force Heavy in action: coordinated DCS World flight
            operations across our NATO-aligned squadrons.
          </p>
        </div>
      </section>

      {/* Video */}
      <main className="w-full px-6 py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div
            className="relative w-full rounded-xl overflow-hidden shadow-2xl border border-gray-200/50"
            style={{ paddingBottom: '56.25%' }}
          >
            {/* Eager, crawlable iframe — NOT the click-to-load facade the home
                page uses. This page exists to get the video indexed, so the
                player must be present in the HTML Googlebot crawls. */}
            <iframe
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?rel=0&modestbranding=1`}
              title="Joint Task Force Heavy: DCS Community Flight Operations"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          <article className="max-w-3xl mx-auto text-gray-700 text-lg leading-relaxed mt-12">
            <p>
              Joint Task Force Heavy (HVY) is a global Digital Combat Simulator
              (DCS World) community operating NATO-aligned squadrons. In this
              video you'll see the coordinated multiplayer flight operations,
              modern-era campaigns, and squadron teamwork that define how we fly,
              from the F-16 and F/A-18 to the A-10, F-14, and rotary wing.
            </p>
            <p className="mt-4">
              Want to fly with us? Learn{' '}
              <Link to="/join-dcs-squadron" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">
                how to join a DCS squadron
              </Link>
              , explore{' '}
              <Link to="/squadrons" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">
                our DCS squadrons
              </Link>
              , or head back to the{' '}
              <Link to="/" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">
                HVY home page
              </Link>
              .
            </p>

            <div className="mt-10 text-center">
              <a
                href="https://discord.gg/sPavY3ZZQH"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackJoinDiscord('watch-page')}
                className="inline-block bg-discord hover:bg-discord/90 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg uppercase tracking-wider"
              >
                Join Our Discord
              </a>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
};

export default WatchPage;
